import { GetStaticProps, InferGetStaticPropsType } from 'next'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'
import { motion } from 'framer-motion'

import { Column, Grid } from '~/components/Grid'
import Layout from '~/components/Layout'
import { PAGE_TITLE_PREFIX } from '~/components/constants'
import { getClient } from '~/lib/sanity.client'
import { urlForImage } from '~/lib/sanity.image'
import type { Navigation, CareersPage, JobListing } from '~/lib/sanity.queries'
import { PortableText } from '@portabletext/react'
import { FadeUpInView } from '~/components/FadeUpInView'
import Eyebrow from '~/components/Eyebrow'
import { Parallax } from '~/components/Parallax'
import PortableTextScrollAnimation from '~/components/TextAnimation'
import Card from '~/components/Card'
import { getCareersPage, getJobListings } from '~/lib/sanity.queries'
import { readToken } from '~/lib/sanity.api'
import { DotGrid } from '~/components/home/GPUPower'
import { DollarSign, MapPin, Shield } from 'lucide-react'

type Props = {
  content: CareersPage
  navigation: Navigation
  jobListings: JobListing[]
}

export const getStaticProps: GetStaticProps<Props> = async ({
  draftMode = false,
}) => {
  const client = getClient(draftMode ? { token: readToken } : undefined)
  const content = await getCareersPage(client)
  const jobListings = await client.fetch(getJobListings)
  const navigation = await client.fetch(`*[_type == "navigation"][0]`)

  return {
    props: {
      content,
      navigation,
      jobListings,
    },
    revalidate: 60,
  }
}

export default function CareersPage({
  content,
  navigation,
  jobListings,
}: Props) {
  const pageTitle = `${PAGE_TITLE_PREFIX} Careers`

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <Layout navigation={navigation}>
        {/* Hero Section */}
        <section className="pt-header md:pt-header-lg">
          <Grid className="flex flex-col items-center justify-center relative h-full min-h-[70vh]">
            <Column className="col-span-12">
              <div className="text-center w-full relative">
                <div className="bg-gradient-blur opacity-20 -z-10"></div>
                <FadeUpInView>
                  <Eyebrow className="mb-8 inline-block m-auto">
                    Join Our Team
                  </Eyebrow>
                </FadeUpInView>
                {content?.hero?.title && (
                  <div className="h2 md:h4 px-10 sm:px-0 md:max-w-5xl text-center lg:h2 text-pretty pb-4 m-auto">
                    <PortableTextScrollAnimation value={content?.hero?.title} />
                  </div>
                )}
                {content?.hero?.body && (
                  <div className="mt-8 body-large md:text-grey text-gradient-grey text-pretty max-w-3xl mx-auto">
                    <PortableTextScrollAnimation value={content?.hero?.body} />
                  </div>
                )}
              </div>
            </Column>
          </Grid>
        </section>

        {/* Video Section */}
        <Parallax speed={1}>
          <motion.div className="h-[300px] md:h-[700px] relative w-full mb-20 md:mb-40 bg-off-black">
            <video
              src="/videos/services-hero.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="absolute w-full h-full object-contain mix-blend-lighten"
              poster="/images/services-hero.webp"
            />
          </motion.div>
        </Parallax>

        {/* Benefits Section */}
        <section className="py-32">
          <Grid>
            <Column className="col-span-12 text-center mb-16">
              <FadeUpInView>
                <h2 className="h2 text-gradient mb-8">
                  {content?.benefits.title}
                </h2>
              </FadeUpInView>
            </Column>
            {content?.benefits?.items?.map((benefit, index) => (
              <Column key={index} className="col-span-12 md:col-span-4">
                <FadeUpInView>
                  <Card className="p-8 h-full">
                    {benefit.icon && (
                      <div className="mb-6 flex justify-center">
                        <Image
                          src={urlForImage(benefit.icon).url()}
                          alt={benefit.title}
                          width={64}
                          height={64}
                          className="opacity-80"
                        />
                      </div>
                    )}
                    <h3 className="h5 text-gradient mb-4 text-center">
                      {benefit.title}
                    </h3>
                    <p className="text-grey text-center">
                      {benefit.description}
                    </p>
                  </Card>
                </FadeUpInView>
              </Column>
            ))}
          </Grid>
        </section>

        {/* Job Listings Section */}
        <section className="py-32 bg-off-black relative">
          <div className="absolute -top-1/4 left-0 w-full h-full">
            <div className="flex gap-3 max-w-1920 m-auto px-4 md:px-16">
              <DotGrid />
            </div>
          </div>
          <Grid>
            <Column className="col-span-12 text-center mb-16">
              <FadeUpInView>
                <h2 className="h2 text-gradient mb-8 max-w-3xl mx-auto">
                  {content?.jobListingsSection?.title}
                </h2>
                {content?.jobListingsSection?.description && (
                  <div className="body-large text-grey max-w-3xl mx-auto">
                    <PortableTextScrollAnimation
                      value={content?.jobListingsSection?.description}
                    />
                  </div>
                )}
              </FadeUpInView>
            </Column>
            {jobListings?.map((job, index) => (
              <Column key={job._id} className="col-span-12 md:col-span-6">
                <FadeUpInView>
                  <Link href={`/careers/${job.slug.current}`}>
                    <Card className="p-8 h-full hover:scale-[1.02] transition-transform duration-300">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="h5 text-gradient">{job.title}</h3>
                      </div>
                      <div className="flex gap-2">
                        <span className="capitalize px-3 py-1 text-sm font-medium bg-accent/10 text-accent rounded-full flex items-center">
                          <Shield className="h-4 w-4 mr-2" />
                          {job.department}
                        </span>
                        <span className="capitalize px-3 py-1 text-sm font-medium bg-accent/10 text-accent rounded-full flex items-center">
                          <MapPin className="h-4 w-4 mr-2" />
                          {job.location}
                        </span>
                        <span className="capitalize px-3 py-1 text-sm font-medium bg-accent/10 text-accent rounded-full flex items-center">
                          <DollarSign className="h-4 w-4 mr-2" />
                          {job.type}
                        </span>
                      </div>
                    </Card>
                  </Link>
                </FadeUpInView>
              </Column>
            ))}
          </Grid>
        </section>

        {/* Culture Section */}
        <section className="py-32">
          <Grid>
            <Column className="col-span-12 text-center">
              <FadeUpInView>
                <h2 className="h2 text-gradient mb-8">
                  {content?.culture?.title}
                </h2>
                <div className="body-large text-grey max-w-3xl mx-auto">
                  <PortableTextScrollAnimation
                    value={content?.culture?.description}
                  />
                </div>
              </FadeUpInView>
            </Column>
          </Grid>
        </section>
      </Layout>
    </>
  )
}
